import AdminListView from "../views/admin/AdminListView";
import AdminRegisterView from "../views/admin/AdminRegisterView";
import AdminEditView from "../views/admin/AdminEditView";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  useLocation,
  Outlet,
  useRouteError,
} from "react-router-dom";
import { ReactNode, useEffect } from "react";
import RootLayout from "../components/layout/RootLayout";
import Header from "../components/layout/Header";
import DashboardView from "../views/home/DashboardView";
import NotFoundView from "../views/NotFoundView";
import ErrorFallback from "../views/ErrorFallback";
import LoginView from "../views/auth/LoginView";
import { isAuthenticated } from "@/lib/api/auth";
import DeviceListView from "@/views/device/DeviceListView";
import DeviceFormView from "@/views/device/DeviceFormView";
import LocationListView from "@/views/location/LocationListView";
import LocationFormView from "@/views/location/LocationFormView";
import StatView from "@/views/stat/StatView";
import StatRegionView from "@/views/stat/StatRegionView";
import StatLocationView from "@/views/stat/StatLocationView";
import DataCriteriaView from "@/views/data/DataCriteriaView";
import MonitoringView from "@/views/monitoring/MonitoringView";
import AlertListView from "@/views/alert/AlertListView";
import AlertAdminView from "@/views/alert/AlertAdminView";
import AlertAdminRegisterView from "@/views/alert/AlertAdminRegisterView";
import AlertAdminEditView from "@/views/alert/AlertAdminEditView";
import AIView from "@/views/ai/AIView";
import ComplaintView from "@/views/complaint/ComplaintView";
import NoticeListView from "../views/notice/NoticeListView";
import NoticeRegisterView from "../views/notice/NoticeRegisterView";
import NoticeEditView from "../views/notice/NoticeEditView";
import SmsTestView from "../views/test/SmsTestView";
// 라우트 경로 상수 정의
export const ROUTES = {
  ROOT: "/",
  LOGIN: "/login",
  DASHBOARD: "/dashboard",
  ADMIN: "/admin",
  ADMIN_REGISTER: "/admin/register",
  DEVICE: "/device",
} as const;

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * 보호된 라우트 컴포넌트
 */
function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

interface PublicRouteProps {
  children: ReactNode;
}

/**
 * 퍼블릭 라우트 컴포넌트
 */
function PublicRoute({ children }: PublicRouteProps) {
  if (isAuthenticated()) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

interface LayoutProps {
  showHeader?: boolean;
  requireAuth?: boolean;
  isDashboard?: boolean;
}

/**
 * 에러 바운더리 래퍼
 */
function ErrorBoundaryWrapper() {
  const error = useRouteError();

  return <ErrorFallback reset={() => (window.location.href = "/")} />;
}

/**
 * 레이아웃 래퍼
 */
function Layout({ showHeader = true, requireAuth = true, isDashboard = false }: LayoutProps) {
  // dashboard 경로일 때 body에 클래스 추가
  useEffect(() => {
    if (isDashboard) {
      document.body.classList.add("dbrd_body");
    } else {
      document.body.classList.remove("dbrd_body");
    }

    // cleanup
    return () => {
      document.body.classList.remove("dbrd_body");
    };
  }, [isDashboard]);

  const content = (
    <>
      {showHeader && <Header />}
      <div className="root-layout">
        <Outlet />
      </div>
    </>
  );

  return requireAuth ? <ProtectedRoute>{content}</ProtectedRoute> : content;
}

/**
 * 라우터 설정
 */
const routerConfig = [
  {
    path: ROUTES.ROOT,
    errorElement: <ErrorBoundaryWrapper />,
    children: [
      // 로그인 경로
      {
        path: "login",
        element: <Layout showHeader={false} requireAuth={false} />,
        children: [
          {
            index: true,
            element: (
              <PublicRoute>
                <LoginView />
              </PublicRoute>
            ),
          },
        ],
      },
      // 대시보드 경로
      {
        path: "dashboard",
        element: <Layout showHeader={true} requireAuth={true} isDashboard={true} />,
        children: [
          {
            index: true,
            element: <DashboardView version="1.0.0" />,
          },
        ],
      },
      {
        path: "monitoring",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <MonitoringView />,
          },
        ],
      },
      {
        path: "ai",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <AIView />,
          },
        ],
      },
      {
        path: "complaint",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <ComplaintView />,
          },
        ],
      },
      {
        path: "alert",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <AlertListView />,
          },
          {
            path: "admin",
            element: <AlertAdminView />,
          },
          {
            path: "admin/register",
            element: <AlertAdminRegisterView />,
          },
          {
            path: "admin/edit/:id",
            element: <AlertAdminEditView />,
          },
        ],
      },
      // 관리자 관련 경로

      {
        path: "notice",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <NoticeListView />,
          },
          {
            path: "register",
            element: <NoticeRegisterView />,
          },
          {
            path: "edit/:id",
            element: <NoticeEditView />,
          },
        ],
      },
      {
        path: "admin",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <AdminListView />,
          },
          {
            path: "register",
            element: <AdminRegisterView />,
          },
          {
            path: ":id/edit",
            element: <AdminEditView />,
          },
        ],
      },
      {
        path: "device",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <DeviceListView />,
          },
          {
            path: "register",
            element: <DeviceFormView />,
          },
          {
            path: "edit/:id",
            element: <DeviceFormView />,
          },
        ],
      },
      {
        path: "stat",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <StatView />,
          },
          {
            path: "region",
            element: <StatRegionView />,
          },
          {
            path: "location",
            element: <StatLocationView />,
          },
        ],
      },
      {
        path: "data",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <DataCriteriaView />,
          },
        ],
      },
      {
        path: "location",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <LocationListView />,
          },
          {
            path: "register",
            element: <LocationFormView />,
          },
          {
            path: "edit/:id",
            element: <LocationFormView />,
          },
        ],
      },
      // SMS 테스트 경로
      {
        path: "sms-test",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <SmsTestView />,
          },
        ],
      },
      // 루트 경로 - 대시보드로 리다이렉트
      {
        path: "/",
        element: <Layout showHeader={true} requireAuth={true} />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.DASHBOARD} replace />,
          },
        ],
      },
    ],
  },
  // 404 처리
  {
    path: "*",
    element: <NotFoundView />,
  },
];

/**
 * 메인 라우터 컴포넌트
 */
export default function RootRouter() {
  const router = createBrowserRouter(routerConfig);
  return <RouterProvider router={router} />;
}
